import React from 'react';
import moment from 'moment-timezone';
import HoverMenu from 'ats/components/hover-menu/HoverMenu';
import { dialogStore } from 'components/layout/modals/DialogStore';
import { notificationStore } from 'components/layout/notifications/NotificationStore';
import { observer } from 'mobx-react';
import { modalStore } from 'components/layout/modals/ModalStore';
import { JobVacancyEdit } from './components/JobVacancyEdit';
import { jobVacancyStore } from './JobVacancyStore';
import Dropdown from 'ats/components/dropdown/Dropdown';
import UserProfileAvatar from 'ats/components/user-profile-avatar/UserProfileAvatar';
import { JobVacancyFillReason } from './components/JobVacancyFillReason';

const DAYS_THRESHOLDS = {
	LOW: 45,
	MEDIUM: 60
};

const DAYS_STYLES = {
	LOW: {
		color: '#34D3B3',
		backgroundColor: '#F2FFFC'
	},
	MEDIUM: {
		color: '#F79009',
		backgroundColor: '#FFFCF5'
	},
	HIGH: {
		color: '#E31B54',
		backgroundColor: '#FFF5F6'
	}
};

const TABLE_COLUMNS = {
	ID: 'id',
	STATUS: 'status',
	REASON: 'cancelReason',
	HIRED: 'jobApplication.applicant.name',
	CREATED_AT: 'createdAt',
	CLOSING_DATE: 'closingDate'
};

const DIALOG_CONFIGS = {
	UPDATE_STATUS: {
		title: 'Update Vacancy Status',
		message: 'The Vacancy Status will be changed. Are you sure?',
		confirmLabel: 'Confirm',
		confirmButtonClass: 'is-primary'
	},
	DELETE: {
		title: 'Delete Job Vacancy',
		message: 'This Job Vacancy entry will be deleted. Are you sure?',
		confirmLabel: 'Delete',
		confirmButtonClass: 'is-danger'
	}
};

const MODAL_CONFIG = {
	title: 'Edit Job Vacancy',
	size: 'xl',
	position: 'center'
};

function JobVacanciesTable({
	data,
	sort,
	onSort,
	onSuccess,
	isEmpty,
	emptyState
}) {
	const getStatus = value => {
		return jobVacancyStore.statusOptions.find(status => status.value === value);
	};

	const getDaysOpen = (createdAt, closingDate) => {
		if (!createdAt) return '-';

		const start = moment(createdAt);
		const end = closingDate ? moment(closingDate) : moment();
		const days = end.diff(start, 'days');

		return days >= 0 ? days : '-';
	};

	const getDaysOpenStyle = days => {
		if (days === '-' || days < 0) return {};

		if (days <= DAYS_THRESHOLDS.LOW) {
			return DAYS_STYLES.LOW;
		}

		if (days <= DAYS_THRESHOLDS.MEDIUM) {
			return DAYS_STYLES.MEDIUM;
		}

		return DAYS_STYLES.HIGH;
	};

	const isColumnSorted = column => {
		if (!sort || sort.column !== column) return null;

		const icon = sort.asc ? 'arrow-up-icon' : 'arrow-down-icon';
		return (
			<span className="sort-icon">
				<img src={`/ats/icons/${icon}.svg`} alt="" />
			</span>
		);
	};

	const handleHeaderClick = column => {
		onSort?.(column);
	};

	const handleVacancyStatusEdit = async (vacancy, status) => {
		try {
			if (status === 'open') {
				dialogStore.openDialog(DIALOG_CONFIGS.UPDATE_STATUS, async () => {
					const result = await jobVacancyStore.update(vacancy.id, {
						jobVacancy: { status }
					});

					if (result.status === 'success') {
						notificationStore.pushSuccessNotification(
							'Job Vacancy Status updated',
							'The Job Vacancy Status has been successfully updated.'
						);
						onSuccess?.();
					} else {
						notificationStore.pushErrorNotification(
							'Update Failed',
							'Failed to update the Job Vacancy Status'
						);
					}
				});

				return;
			}

			modalStore.openModal(
				{
					title: 'Fill Reason',
					size: 'xl',
					position: 'center'
				},
				<JobVacancyFillReason
					vacancy={vacancy}
					status={status}
					handleSuccess={onSuccess}
				/>
			);
		} catch (error) {
			console.error('Error updating vacancy status:', error);
			notificationStore.pushErrorNotification(
				'Update Failed',
				'An unexpected error occurred while updating the status'
			);
		}
	};

	const handleVacancyEdit = vacancy => {
		modalStore.openModal(
			MODAL_CONFIG,
			<JobVacancyEdit vacancy={vacancy} handleSuccess={onSuccess} />
		);
	};

	const handleVacancyDelete = async vacancy => {
		try {
			dialogStore.openDialog(DIALOG_CONFIGS.DELETE, async () => {
				const result = await jobVacancyStore.delete(vacancy.id);

				if (result.status === 'success') {
					notificationStore.pushSuccessNotification(
						'Job Vacancy Deleted',
						'The Job Vacancy has been successfully deleted.'
					);
					onSuccess?.();
				} else {
					notificationStore.pushErrorNotification(
						'Delete Failed',
						'Failed to delete the Job Vacancy'
					);
				}
			});
		} catch (error) {
			console.error('Error deleting vacancy:', error);
			notificationStore.pushErrorNotification(
				'Delete Failed',
				'An unexpected error occurred while deleting the vacancy'
			);
		}
	};

	const getMenuItems = vacancy => [
		{
			label: 'Edit',
			onClick: () => handleVacancyEdit(vacancy)
		},
		{
			label: 'Delete',
			onClick: () => handleVacancyDelete(vacancy)
		}
	];

	const getStatusOptions = () => {
		return jobVacancyStore.statusOptions
			.filter(status => status.value !== 'filled')
			.map(status => ({
				...status,
				label: status.text,
				className: status.value.replace(/_/g, '-')
			}));
	};

	const renderTableHeader = () => (
		<thead>
			<tr>
				<th
					style={{ width: '13%' }}
					className="is-sortable"
					onClick={() => handleHeaderClick(TABLE_COLUMNS.ID)}>
					ID {isColumnSorted(TABLE_COLUMNS.ID)}
				</th>
				<th
					style={{ width: '20%' }}
					className="is-sortable"
					onClick={() => handleHeaderClick(TABLE_COLUMNS.STATUS)}>
					Status {isColumnSorted(TABLE_COLUMNS.STATUS)}
				</th>
				<th
					style={{ width: '20%' }}
					className="is-sortable"
					onClick={() => handleHeaderClick(TABLE_COLUMNS.REASON)}>
					Reason {isColumnSorted(TABLE_COLUMNS.REASON)}
				</th>
				<th
					style={{ width: '30%' }}
					className="is-sortable"
					onClick={() => handleHeaderClick(TABLE_COLUMNS.HIRED)}>
					Hired {isColumnSorted(TABLE_COLUMNS.HIRED)}
				</th>
				<th
					style={{ width: '20%' }}
					className="is-sortable"
					onClick={() => handleHeaderClick(TABLE_COLUMNS.CREATED_AT)}>
					Created At {isColumnSorted(TABLE_COLUMNS.CREATED_AT)}
				</th>
				<th
					style={{ width: '13%' }}
					className="is-sortable"
					onClick={() => handleHeaderClick(TABLE_COLUMNS.CLOSING_DATE)}>
					Days open {isColumnSorted(TABLE_COLUMNS.CLOSING_DATE)}
				</th>
				<th style={{ width: '4%' }}></th>
			</tr>
		</thead>
	);

	const renderTableRow = row => {
		const statusData = getStatus(row.status);
		const daysOpen = getDaysOpen(row.createdAt, row.closingDate);
		const cancelReasonData = jobVacancyStore.getCancelReasonByValue(
			row.cancelReason
		);

		return (
			<tr key={row.id}>
				<td>{row.id}</td>
				<td className="status-column">
					<div
						className={`status ${statusData?.value?.replace(/_/g, '-') || ''}`}>
						<Dropdown
							value={{ ...statusData, label: statusData?.text }}
							name="status"
							hideSelectedOption
							onChange={value => handleVacancyStatusEdit(row, value.value)}
							options={getStatusOptions()}
							disabled={statusData.value === 'cancelled'}
						/>
					</div>
				</td>
				<td>{cancelReasonData?.text}</td>
				<td>
					{row.jobApplication?.applicant && (
						<UserProfileAvatar
							imageUrl={
								row.jobApplication?.applicant?.imageUrl ||
								row.jobApplication?.applicant?.candidate?.imageUrl
							}
							name={row.jobApplication?.applicant?.name}
							showName
						/>
					)}
				</td>
				<td>{moment(row.createdAt).format('ll')}</td>
				<td className="days-open-column">
					<span style={getDaysOpenStyle(daysOpen)}>{daysOpen}</span>
				</td>
				<td className="has-text-centered action-btn">
					<HoverMenu
						id={row.id}
						position="left"
						buttonIcon={
							<img src="/ats/icons/three-dots-icon.svg" alt="Menu button" />
						}
						items={getMenuItems(row)}
					/>
				</td>
			</tr>
		);
	};

	return (
		<>
			<table className="table is-fullwidth">
				{renderTableHeader()}
				{!isEmpty ? <tbody>{data.map(renderTableRow)}</tbody> : null}
			</table>
			{isEmpty ? emptyState : null}
		</>
	);
}

export default observer(JobVacanciesTable);
